import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  History,
  Users,
  Send,
  Facebook,
  Workflow,
  Wallet,
  Settings,
  Puzzle,
  Tag,
  FolderKanban,
  UserCircle,
  LogOut,
  Menu,
  X,
  Database,
  FileText,
  Megaphone,
  MessageCircle,
  Contact,
  Download,
} from "lucide-react";
import logo from "../../assets/i3.png";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();

  const isActive = (paths = []) => {
    return paths.some((p) => location.pathname.startsWith(p));
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const menuItems = [
    {
      path: "/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
    },
    {
      path: "/waba",
      icon: Users,
      label: "Whatsapp Numbers",
    },
    {
      path: "/templates",
      icon: FileText,
      label: "Templates",
    },
    {
      path: "/broadcast",
      icon: Megaphone,
      label: "Campaigns",
    },
    {
      path: "/contact-lists",
      icon: Contact,
      label: "Contact Lists",
    },
    {
      path: "/flow-list",
      icon: Workflow,
      label: "Flows",
    },
    {
      path: "/live-chats",
      icon: MessageCircle,
      label: "Live Chats",
    },
    {
      path: "/download-center",
      icon: Download,
      label: "Downloads",
    },
    // {
    //   path: "/docs",
    //   icon: FolderKanban,
    //   label: "API Docs"
    // },
  ];

  const bottomMenuItems = [
    {
      path: "/profile",
      icon: UserCircle,
      label: "Profile",
    },
    {
      path: "/settings",
      icon: Settings,
      label: "Settings",
    },
    {
      path: "#",
      icon: LogOut,
      label: "Logout",
      onClick: handleLogout,
      isLogout: true,
    },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          w-20 h-screen fixed left-0 top-0 z-50 transform transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          flex flex-col
        `}
        style={{ backgroundColor: "rgb(4, 138, 173)" }}
      >
        {/* Logo */}
        <div className="py-2 px-2 flex items-center justify-center border-b border-white/10 relative">
          <img src={logo} alt="BluTec PING" className="h-8 w-8 rounded-lg" />
          <button
            onClick={toggleSidebar}
            className="absolute right-1.5 text-white lg:hidden"
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 scrollbar-hide flex flex-col">
          <ul className="space-y-0.5 flex-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.paths || [item.path]);

              return (
                <li key={item.path} className="relative">
                  <Link
                    to={item.path}
                    className={`
                      flex flex-col items-center justify-center py-2 px-1.5 relative group
                      transition-all duration-200
                      ${
                        active
                          ? "text-[rgb(4,138,173)]"
                          : "text-white/70 hover:text-white"
                      }
                    `}
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                  >
                    {/* Active indicator line */}
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
                    )}

                    {/* Icon container */}
                    <div
                      className={`
                        rounded-lg p-1.5 mb-0.5 transition-all duration-200
                        ${
                          active
                            ? "bg-white shadow-lg"
                            : "bg-transparent group-hover:bg-white/10"
                        }
                      `}
                    >
                      <Icon size={18} strokeWidth={active ? 2.5 : 2} />
                    </div>

                    {/* Label */}
                    <span
                      className={`
                      text-[10px] font-medium text-center leading-tight max-w-full px-0.5
                      ${active ? "text-white" : ""}
                    `}
                    >
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Bottom Menu Items (Profile, Settings, Logout) */}
          <ul className="space-y-0.5 border-t border-white/10 pt-2">
            {bottomMenuItems.map((item) => {
              const Icon = item.icon;
              const active = !item.isLogout && isActive([item.path]);

              if (item.isLogout) {
                return (
                  <li key="logout" className="relative">
                    <button
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          toggleSidebar();
                        }
                        handleLogout();
                      }}
                      className={`
                        w-full flex flex-col items-center justify-center py-2 px-1.5 relative group
                        transition-all duration-200
                        text-white/70 hover:text-white
                      `}
                    >
                      {/* Icon container */}
                      <div
                        className={`
                          rounded-lg p-1.5 mb-0.5 transition-all duration-200
                          bg-transparent group-hover:bg-white/10
                        `}
                      >
                        <Icon
                          size={18}
                          strokeWidth={2}
                          className="text-red-300"
                        />
                      </div>

                      {/* Label */}
                      <span className="text-[10px] font-medium text-center leading-tight max-w-full px-0.5 text-red-300">
                        {item.label}
                      </span>
                    </button>
                  </li>
                );
              }

              return (
                <li key={item.path} className="relative">
                  <Link
                    to={item.path}
                    className={`
                      flex flex-col items-center justify-center py-2 px-1.5 relative group
                      transition-all duration-200
                      ${
                        active
                          ? "text-[rgb(4,138,173)]"
                          : "text-white/70 hover:text-white"
                      }
                    `}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        toggleSidebar();
                      }
                      if (item.onClick) {
                        item.onClick();
                      }
                    }}
                  >
                    {/* Active indicator line */}
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
                    )}

                    {/* Icon container */}
                    <div
                      className={`
                        rounded-lg p-1.5 mb-0.5 transition-all duration-200
                        ${
                          active
                            ? "bg-white shadow-lg"
                            : "bg-transparent group-hover:bg-white/10"
                        }
                      `}
                    >
                      <Icon size={18} strokeWidth={active ? 2.5 : 2} />
                    </div>

                    {/* Label */}
                    <span
                      className={`
                      text-[10px] font-medium text-center leading-tight max-w-full px-0.5
                      ${active ? "text-white" : ""}
                    `}
                    >
                      {item.label}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* Mobile toggle button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-3 left-3 z-40 lg:hidden text-white p-1.5 rounded-lg shadow-lg"
        style={{ backgroundColor: "rgb(4, 138, 173)" }}
        aria-label="Toggle sidebar"
      >
        <Menu size={20} />
      </button>
    </>
  );
};

export default Sidebar;
